import titleize from 'lib/formatters/titleize';
import { PARTICIPANT_SOURCE_METADATA } from 'lib/generated_constants/stats_constants';

import type {
  Option,
  BlankFilter,
  FilterGroup,
  FilterSchema,
  FilterSchemaGroup,
} from './types';

export const filterType = {
  multiSelect: 'multiSelect',
  date: 'date',
  number: 'number',
  boolean: 'boolean',
  string: 'string',
  aggregate: 'aggregate',
  select: 'select',
  range: 'range',
  staleScreener: 'staleScreener',
} as const;

export const logicalOperator = {
  and: 'and',
  or: 'or',
} as const;

export const operator = {
  after: 'after',
  before: 'before',
  contains: 'contains',
  empty: 'empty',
  equals: 'equals',
  equalsCaseInsensitive: 'equalsCaseInsensitive',
  exclude: 'exclude',
  greaterThan: 'greaterThan',
  greaterThanDaysAgo: 'greaterThanDaysAgo',
  greaterThanOrEquals: 'greaterThanOrEquals',
  include: 'include',
  lessThan: 'lessThan',
  lessThanDaysAgo: 'lessThanDaysAgo',
  lessThanOrEquals: 'lessThanOrEquals',
  notContains: 'notContains',
  notEmpty: 'notEmpty',
  notEquals: 'notEquals',
  notEqualsCaseInsensitive: 'notEqualsCaseInsensitive',
  between: 'between',
} as const;

export const sourceValue = {
  api: 'api',
  csv: 'csv',
  optIn: 'optIn',
  project: 'project',
  researcher: 'researcher',
  staff: 'staff',
} as const;

export const dateRange = {
  days: 'days',
  months: 'months',
  years: 'years',
} as const;

export const dateRangeLabels = {
  days: 'days ago',
  months: 'months ago',
  years: 'years ago',
} as const;

export const timeFrame = {
  fromAllTime: 'fromAllTime',
  in: 'in',
} as const;

export const filterKeys = {
  answers: 'answers',
  email: 'email',
  createdAt: 'created_at',
  firstName: 'first_name',
  labels: 'label_ids',
  lastAppliedAt: 'last_applied_at',
  lastInvitedAt: 'last_invited_at',
  lastParticipatedAt: 'last_participated_at',
  lastBulkEmailSentAt: 'last_bulk_email_sent_at',
  lastName: 'last_name',
  optInForm: 'opt_in_form_ids',
  phoneNumber: 'phone_number',
  populationPanel: 'population_panel_ids',
  projectParticipantTags: 'project_tag_ids',
  projectParticipation: 'participated_project_ids',
  source: 'source',
  status: 'status_id',
  timezone: 'timezone',
  yearlyPayments: 'yearly_payments',
  unsubscribed: 'unsubscribed',
  age: 'age',
};

export const operatorLabels = {
  is: 'Is',
  isNot: 'Is not',
  equals: 'Equals',
  doesNotEqual: 'Does not equal',
  isBefore: 'Is before',
  isAfter: 'Is after',
  isMoreThan: 'Is more than',
  isLessThan: 'Is less than',
  isLessThanOrEqualTo: 'Is less than or equal to',
  isGreaterThan: 'Is greater than',
  isGreaterThanOrEqualTo: 'Is greater than or equal to',
  contains: 'Contains',
  doesNotContain: 'Does not contain',
  isEmpty: 'Is empty',
  isNotEmpty: 'Is not empty',
  isOneOf: 'Is one of',
  isNotOneOf: 'Is not one of',
  participatedIn: 'Participated in',
  didNotParticipateIn: 'Did not participate in',
  hasCompleted: 'Has completed',
  hasNotCompleted: 'Has not completed',
  include: 'Includes',
  exclude: 'Excludes',
  isBetween: 'Is between',
};

export const placeholderText = {
  attribute: 'Column',
  booleanValue: 'Value',
  defaultHeader: 'In this view, show records',
  defaultSubmitButton: 'Apply',
  noResults: 'No results',
  multiSelectDefault: 'Select...',
  numericValue: 'Enter number',
  numericMin: 'Min',
  numericMax: 'Max',
  operator: 'Condition',
  optInFormValue: 'Select an opt-in form',
  participantSourceValue: 'Select a source',
  populationPanelValue: 'Select a panel',
  projectHistoryValue: 'Select a project',
  projectParticipantTagsValue: 'Select tags',
  quotaAttribute: 'Attribute',
  quotasHeader: 'Set quota conditions',
  search: 'Search',
  segmentCreateButton: 'Save segment',
  segmentUpdateButton: 'Update segment',
  stringValue: 'Value',
  timezoneValue: 'Select a timezone',
  typeToSearch: 'Type to search...',
};

export const defaultFiltersSchema: FilterSchema[] = [
  { value: filterKeys.createdAt, label: 'Date added', type: filterType.date },
  { value: filterKeys.email, label: 'Email', type: filterType.string },
  { value: filterKeys.firstName, label: 'First name', type: filterType.string },
  { value: filterKeys.source, label: 'First source', type: filterType.select },
  {
    value: filterKeys.yearlyPayments,
    label: 'Incentives earned',
    type: filterType.aggregate,
  },
  { value: filterKeys.labels, label: 'Labels', type: filterType.multiSelect },
  {
    value: filterKeys.lastAppliedAt,
    label: 'Last applied date',
    type: filterType.date,
  },
  {
    value: filterKeys.lastBulkEmailSentAt,
    label: 'Last bulk email received date',
    type: filterType.date,
  },
  {
    value: filterKeys.lastInvitedAt,
    label: 'Last invited date',
    type: filterType.date,
  },
  { value: filterKeys.lastName, label: 'Last name', type: filterType.string },
  {
    value: filterKeys.lastParticipatedAt,
    label: 'Last participated date',
    type: filterType.date,
  },
  {
    value: filterKeys.optInForm,
    label: 'Opt-in form history',
    type: filterType.select,
  },
  {
    value: filterKeys.populationPanel,
    label: 'Panel',
    type: filterType.select,
  },
  {
    value: filterKeys.phoneNumber,
    label: 'Phone number',
    type: filterType.string,
  },
  {
    value: filterKeys.projectParticipation,
    label: 'Project history',
    type: filterType.select,
  },
  { value: filterKeys.timezone, label: 'Timezone', type: filterType.select },
  {
    value: filterKeys.unsubscribed,
    label: 'Unsubscribed',
    type: filterType.boolean,
  },
];

export const workspaceSchemaGroupLabels = {
  annotations: 'annotations',
  projectData: 'project data',
  recruitmentCharacteristics: 'recruitment characteristics',
  screener: 'screener (latest)',
};

export const defaultWorkspaceFiltersSchema: FilterSchemaGroup[] = [
  {
    label: workspaceSchemaGroupLabels.annotations,
    options: [
      {
        value: filterKeys.projectParticipantTags,
        label: 'Tags',
        type: filterType.multiSelect,
      },
    ],
  },
  {
    label: workspaceSchemaGroupLabels.projectData,
    options: [
      {
        value: filterKeys.status,
        label: 'Status',
        type: filterType.multiSelect,
      },
    ],
  },
  {
    label: workspaceSchemaGroupLabels.recruitmentCharacteristics,
    options: [
      {
        value: filterKeys.age,
        label: 'Age',
        type: filterType.range,
      },
    ],
  },
];

export const piiAttributes = [
  filterKeys.email,
  filterKeys.lastName,
  filterKeys.phoneNumber,
];

export const emptinessOperators = [operator.empty, operator.notEmpty];
export const containsOperators = [operator.contains, operator.notContains];

export const logicalOperatorOptions: Option[] = [
  { label: titleize(logicalOperator.and), value: logicalOperator.and },
  { label: titleize(logicalOperator.or), value: logicalOperator.or },
];

export const emptinessOperatorOptions: Option[] = [
  { label: operatorLabels.isEmpty, value: operator.empty },
  { label: operatorLabels.isNotEmpty, value: operator.notEmpty },
];

export const stringOperatorOptions: Option[] = [
  { label: operatorLabels.contains, value: operator.contains },
  { label: operatorLabels.doesNotContain, value: operator.notContains },
  { label: operatorLabels.is, value: operator.equalsCaseInsensitive },
  { label: operatorLabels.isNot, value: operator.notEqualsCaseInsensitive },
  ...emptinessOperatorOptions,
];

export const numericOperatorOptions: Option[] = [
  { label: operatorLabels.equals, value: operator.equals },
  { label: operatorLabels.doesNotEqual, value: operator.notEquals },
  { label: operatorLabels.isGreaterThan, value: operator.greaterThan },
  {
    label: operatorLabels.isGreaterThanOrEqualTo,
    value: operator.greaterThanOrEquals,
  },
  { label: operatorLabels.isLessThan, value: operator.lessThan },
  {
    label: operatorLabels.isLessThanOrEqualTo,
    value: operator.lessThanOrEquals,
  },
  ...emptinessOperatorOptions,
];

export const selectOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equalsCaseInsensitive },
  { label: operatorLabels.isNot, value: operator.notEqualsCaseInsensitive },
];

export const firstSourceOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equals },
  { label: operatorLabels.isNot, value: operator.notEquals },
];

export const projectHistoryOperatorOptions: Option[] = [
  { label: operatorLabels.participatedIn, value: operator.equals },
  { label: operatorLabels.didNotParticipateIn, value: operator.notEquals },
];

export const optInFormsOperatorOptions: Option[] = [
  { label: operatorLabels.hasCompleted, value: operator.equals },
  { label: operatorLabels.hasNotCompleted, value: operator.notEquals },
];

export const timezoneOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equals },
  { label: operatorLabels.isNot, value: operator.notEquals },
  ...emptinessOperatorOptions,
];

export const getPopulationPanelOperatorOptions = (
  panelId?: string | null,
): Option[] => [
  { label: operatorLabels.is, value: operator.equals },
  { label: operatorLabels.isNot, value: operator.notEquals },
  ...(panelId ? [] : emptinessOperatorOptions),
];

export const participantSourceOptions: Option[] = Object.values(
  PARTICIPANT_SOURCE_METADATA,
).map(({ label, id }) => ({ label: label, value: id }));

export const booleanOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equals },
  ...emptinessOperatorOptions,
];

export const booleanValueOptions: Option[] = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
];

export const dateOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equals },
  { label: operatorLabels.isBefore, value: operator.before },
  { label: operatorLabels.isAfter, value: operator.after },
  { label: operatorLabels.isLessThan, value: operator.lessThanDaysAgo },
  { label: operatorLabels.isMoreThan, value: operator.greaterThanDaysAgo },
  { label: operatorLabels.isEmpty, value: operator.empty },
  { label: operatorLabels.isNotEmpty, value: operator.notEmpty },
];

export const dateRangeOptions: Option[] = [
  { label: dateRangeLabels.days, value: dateRange.days },
];

export const aggregateOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equals },
  { label: operatorLabels.isNot, value: operator.notEquals },
  { label: operatorLabels.isEmpty, value: operator.empty },
  { label: operatorLabels.isNotEmpty, value: operator.notEmpty },
  { label: operatorLabels.isGreaterThan, value: operator.greaterThan },
  {
    label: operatorLabels.isGreaterThanOrEqualTo,
    value: operator.greaterThanOrEquals,
  },
  { label: operatorLabels.isLessThan, value: operator.lessThan },
  {
    label: operatorLabels.isLessThanOrEqualTo,
    value: operator.lessThanOrEquals,
  },
];

export const multiSelectOperatorOptions: Option[] = [
  { label: operatorLabels.include, value: operator.include },
  { label: operatorLabels.exclude, value: operator.exclude },
];

export const recruitmentCharacteristicOperatorOptions: Option[] = [
  ...multiSelectOperatorOptions,
  // TODO: Uncomment emptiness operators when backend filter schema supports them
  // ...emptinessOperatorOptions,
];

export const rangeOperatorOptions: Option[] = [
  { label: operatorLabels.is, value: operator.equals },
  { label: operatorLabels.isNot, value: operator.notEquals },
  { label: operatorLabels.isGreaterThan, value: operator.greaterThan },
  {
    label: operatorLabels.isGreaterThanOrEqualTo,
    value: operator.greaterThanOrEquals,
  },
  { label: operatorLabels.isLessThan, value: operator.lessThan },
  {
    label: operatorLabels.isLessThanOrEqualTo,
    value: operator.lessThanOrEquals,
  },
  { label: operatorLabels.isBetween, value: operator.between },
];

export const blankFilter: BlankFilter = {
  attribute: null,
  operator: null,
  type: null,
  value: '',
};

export const blankFilterGroup: FilterGroup = {
  logicalOperator: logicalOperator.and,
  filters: [blankFilter],
};
